import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LeadFunnelCard } from '../../components/speedToLead/LeadFunnelCard';
import { formatSpeed } from '../../lib/leads/leadList';
import { ACCOUNT_SUB_TABS, resolveSubTab, type AccountSubTab } from '../../lib/kyckConnect/links';
import { SUMMARY_DAYS, type SummaryDays } from '../../types/kyckConnect';
import { useCurrentUser } from '../auth';
import { ClubCalls } from '../speedToLead/ClubCalls';
import { LeadsSection } from '../speedToLead/LeadsSection';
import { fetchSetup, fetchSummary } from './api';
import { SetupChecklist } from './SetupChecklist';

// THE ACCOUNT PAGE'S KYCKCONNECT TAB (redesign Phase 1; it REPLACED the Leads tab). Four sub-tabs: Dashboard, Leads, Calls, Setup.
// The sub-tab and the open setup step live in the URL (?sub=, ?step=), so a link opens exactly where it points.
//
// Nothing here writes by itself and nothing fires on render except the reads for the sub-tab on screen (Radix unmounts the others).
// The look is the staff app's own: the existing Tabs, Badge and the Leads/Calls components.

function Figure({ label, value, note, testId }: { label: string; value: string; note?: string; testId: string }) {
  return (
    <div className="min-w-[8rem] flex-1 rounded border border-border bg-background p-3" data-testid={testId}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

function Dashboard({ accountId }: { accountId: string }) {
  const [days, setDays] = useState<SummaryDays>(30);
  const query = useQuery({ queryKey: ['kyckconnect', 'summary', accountId, days], queryFn: () => fetchSummary(accountId, days) });
  const f = query.data?.funnel;
  const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 100)}% of leads` : undefined);
  return (
    <div className="space-y-4" data-testid="kc-dashboard">
      <div className="flex items-center gap-2 text-sm">
        <label htmlFor="kc-days" className="text-slate-600">Period</label>
        <select id="kc-days" className="rounded-md border border-slate-300 bg-white p-1.5 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value) as SummaryDays)}>
          {SUMMARY_DAYS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      {query.isLoading && <Spinner />}
      {query.error && <p className="text-sm text-red-600">{query.error instanceof Error ? query.error.message : 'Could not load the figures.'}</p>}
      {f && query.data && (
        <>
          <div className="flex flex-wrap gap-3">
            <Figure testId="kc-leads" label="Leads" value={String(f.leads)} note="test leads left out" />
            <Figure testId="kc-contacted" label="Contacted" value={String(f.contacted)} note={pct(f.contacted, f.leads)} />
            <Figure testId="kc-speed" label="Median speed to lead" value={formatSpeed(f.medianSpeedSeconds)} note={f.respondedCount > 0 ? `over ${f.respondedCount} responded to` : 'nobody responded to yet'} />
            <Figure testId="kc-booked" label="Booked" value={String(f.committed)} note="Call back or tour booked, or joined" />
            <Figure testId="kc-joined" label="Joined" value={String(f.joined)} note={pct(f.joined, f.leads)} />
            <Figure testId="kc-needs-person" label="Needs a person" value={String(query.data.needsPerson)} note="right now, any age" />
          </div>
          <LeadFunnelCard funnel={f} />
        </>
      )}
    </div>
  );
}

export function KyckConnectTab({ accountId }: { accountId: string }) {
  const me = useCurrentUser();
  const isAdmin = me.role === 'admin';
  const [params, setParams] = useSearchParams();
  const sub = resolveSubTab(params.get('sub'));
  const setup = useQuery({ queryKey: ['kyckconnect', 'setup', accountId], queryFn: () => fetchSetup(accountId) });

  const setSub = (id: AccountSubTab) => {
    const next = new URLSearchParams(params);
    next.set('sub', id);
    if (id !== 'setup') next.delete('step');
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="kyckconnect-tab">
      <div className="flex flex-wrap items-center gap-2" data-testid="kc-badges">
        <span className="text-sm font-medium text-slate-700">Services</span>
        {setup.data && setup.data.badges.length > 0 ? (
          setup.data.badges.map((b) => <Badge key={b.id} variant="success">{b.label}</Badge>)
        ) : (
          <span className="text-sm text-slate-500">{setup.isLoading ? 'Checking…' : 'None switched on yet. Open Setup to start.'}</span>
        )}
      </div>
      <Tabs value={sub} onValueChange={(v) => setSub(v as AccountSubTab)}>
        <TabsList>
          {ACCOUNT_SUB_TABS.map((t) => <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="dashboard"><Dashboard accountId={accountId} /></TabsContent>
        <TabsContent value="leads"><LeadsSection accountId={accountId} /></TabsContent>
        <TabsContent value="calls"><ClubCalls clubId={accountId} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="setup"><SetupChecklist accountId={accountId} isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}
